import { calendarSocketManager } from "../../services/appRegistry";
import { socketRequest } from "../../services/socketRequest";
import {
  OccurrenceMetadata,
  ScheduleItemCreateDto,
  ScheduleItemDeleteDto,
  ScheduleItemDto,
  ScheduleItemUpdateDto,
} from "./calendar.types";

const request = <T>(
  event: string,
  payload?: unknown,
  options?: Parameters<typeof socketRequest<T>>[3],
) => socketRequest<T>(calendarSocketManager, event, payload, options);

const createScheduleItem = (
  props: ScheduleItemCreateDto,
): Promise<ScheduleItemDto> => request("createScheduleItem", { props });

const createScheduleItemNote = (
  props: ScheduleItemCreateDto,
): Promise<ScheduleItemDto> =>
  request("createScheduleItemNote", { id: props.noteId, props });

const getScheduleItems = (): Promise<ScheduleItemDto[]> =>
  request("getScheduleItems");

const getRoutines = (): Promise<ScheduleItemDto[]> =>
  request("getRoutines", undefined, {
    // The gateway answers failures with the string "Error" instead of
    // emitting "error", so anything but an array is a failed request.
    transform: (response) => {
      if (!Array.isArray(response)) throw new Error("Failed to load routines");
      return response;
    },
  });

const getCalendarItems = (
  timeMin?: string,
  timeMax?: string,
): Promise<ScheduleItemDto[]> =>
  request("getCalendarItems", { timeMin, timeMax });

const getScheduleItem = (scheduleItemId: string): Promise<ScheduleItemDto> =>
  request("getScheduleItem", { calendarItemId: scheduleItemId });

const getGoogleCalendarEntries = (
  timeMin: string,
  timeMax: string,
): Promise<ScheduleItemDto[]> =>
  request("getGoogleCalendarEntries", { timeMin, timeMax });

const getMicrosoftCalendarEntries = (
  timeMin: string,
  timeMax: string,
): Promise<ScheduleItemDto[]> =>
  request("getMicrosoftCalendarEntries", { timeMin, timeMax });

const getGoogleCalendarEntry = (eventId: string): Promise<any> =>
  request("getGoogleCalendarEntry", { eventId });

/**
 * Occurrence metadata (`originalStart`, `seriesStart`, `seriesEnd`) exists only
 * on the instances the client expands out of a recurring series — see
 * `parseCalendarEvents`. The gateway spreads whatever it receives straight into
 * `prisma.scheduleItem.update`, so these have to be dropped before they go over
 * the socket; callers that build a payload by spreading a rendered item would
 * otherwise send columns that do not exist.
 */
const stripOccurrenceMetadata = (
  props: ScheduleItemUpdateDto,
): ScheduleItemUpdateDto => {
  const persistable: ScheduleItemUpdateDto & OccurrenceMetadata = { ...props };
  delete persistable.originalStart;
  delete persistable.seriesStart;
  delete persistable.seriesEnd;
  return persistable;
};

const updateScheduleItem = (
  props: ScheduleItemUpdateDto,
): Promise<ScheduleItemDto> =>
  request(
    props.origin === "google"
      ? "updateScheduleItemGoogle"
      : "updateScheduleItem",
    {
      calendarItemId: props.id,
      props: stripOccurrenceMetadata(props),
    },
    // Google updates are answered on the same event
    { answerEvent: "updateScheduleItemAnswer" },
  );

const updateScheduleItemNote = (
  props: ScheduleItemUpdateDto,
): Promise<ScheduleItemDto> =>
  request("updateScheduleItemNote", {
    id: props.noteId,
    props: stripOccurrenceMetadata(props),
  });

const deleteScheduleItem = (
  props: ScheduleItemDeleteDto,
): Promise<ScheduleItemDeleteDto> =>
  request("deleteScheduleItem", { calendarItemId: props.id, props });

const deleteScheduleItemNote = (
  props: ScheduleItemDeleteDto,
): Promise<ScheduleItemDeleteDto> => {
  if (!props.noteId) return Promise.reject("Rejected: No note Id");
  return request("deleteScheduleItemNote", { id: props.noteId, props });
};

const calendarService = {
  getGoogleCalendarEntry,
  updateScheduleItem,
  deleteScheduleItem,
  getScheduleItems,
  getCalendarItems,
  getRoutines,
  createScheduleItem,
  updateScheduleItemNote,
  deleteScheduleItemNote,
  createScheduleItemNote,
  getGoogleCalendarEntries,
  getMicrosoftCalendarEntries,
  getScheduleItem,
};
export default calendarService;
